"use client";

import Link from "next/link";
import { useActionState, useState } from "react";

import { register, type RegisterState } from "./actions";

const mono = "font-paw-mono";
const label = `mb-2 block ${mono} text-[11px] tracking-[0.16em] text-(--paw-steel-faint)`;

const ROLES = ["PUP", "HANDLER", "LEATHER", "FURRY", "KINKSTER"];

export function RegisterForm({
  prefillHandle,
  defaultRole,
  inviteOnly,
}: {
  prefillHandle: string;
  defaultRole: string;
  inviteOnly: boolean;
}) {
  const [state, formAction, pending] = useActionState<RegisterState, FormData>(
    register,
    null,
  );
  const [domain, setDomain] = useState("1");
  const [role, setRole] = useState(defaultRole.toUpperCase());

  return (
    <form
      action={formAction}
      className="flex flex-col"
      style={{ maxWidth: "28rem", gap: "20px" }}
    >
      <div>
        <label htmlFor="name" className={label}>
          NAME
        </label>
        <input
          id="name"
          name="name"
          type="text"
          required
          autoFocus
          placeholder="What people call you"
          className="paw-field"
        />
      </div>

      <div>
        <span className={label}>HANDLE</span>
        {/* site_domain_id is display-only until the site_domains table is wired. */}
        <div
          className="flex"
          style={{
            border: "2px solid var(--paw-signal)",
            background: "var(--paw-panel)",
          }}
        >
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className={`shrink-0 border-0 border-r bg-(--paw-ink) px-2.5 text-[13px] text-(--paw-steel-dim)`}
            style={{ height: "48px", borderColor: "var(--paw-line-strong)" }}
          >
            <option value="1">collr.app/</option>
            <option value="2">pawpup.app/</option>
          </select>
          <input
            id="handle"
            name="handle"
            type="text"
            required
            defaultValue={prefillHandle}
            placeholder="yourhandle"
            className="min-w-0 flex-1 border-0 bg-transparent px-3.5 py-3 text-[14px] text-white focus:outline-none"
          />
        </div>
        <p className={`mt-2 ${mono} text-[11px] tracking-[0.06em] text-[#5b6068]`}>
          YOUR PAGE WILL LIVE AT{" "}
          {domain === "1" ? "COLLR.APP/" : "PAWPUP.APP/"}YOURHANDLE
        </p>
      </div>

      <div>
        <span className={label}>ROLE</span>
        <input type="hidden" name="role" value={role.toLowerCase()} />
        <div className="flex flex-wrap gap-2">
          {ROLES.map((r) => {
            const on = role === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setRole(r)}
                className={`cursor-pointer border px-4 py-2.5 ${mono} text-[12px] tracking-[0.1em] transition ${
                  on
                    ? "border-(--paw-signal) text-(--paw-paper)"
                    : "border-(--paw-line-strong) bg-(--paw-panel) text-(--paw-steel-dim) hover:border-(--paw-signal) hover:text-(--paw-paper)"
                }`}
              >
                {r}
              </button>
            );
          })}
        </div>
        <p className={`mt-2 ${mono} text-[11px] tracking-[0.06em] text-[#5b6068]`}>
          SWITCH IT WHENEVER YOU LIKE FROM YOUR PROFILE
        </p>
      </div>

      <div>
        <label htmlFor="email" className={label}>
          EMAIL
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="you@example.com"
          className="paw-field"
        />
      </div>

      {inviteOnly ? (
        <div>
          <label htmlFor="invite_code" className={label}>
            INVITE CODE
          </label>
          <input
            id="invite_code"
            name="invite_code"
            type="text"
            required
            placeholder="Enter your invite code"
            className="paw-field"
          />
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3.5">
        <div>
          <label htmlFor="password" className={label}>
            PASSWORD
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            className="paw-field"
          />
        </div>
        <div>
          <label htmlFor="password_confirmation" className={label}>
            CONFIRM
          </label>
          <input
            id="password_confirmation"
            name="password_confirmation"
            type="password"
            required
            className="paw-field"
          />
        </div>
      </div>

      {state?.error ? (
        <div
          role="alert"
          className={`border border-(--paw-danger) bg-(--paw-danger-wash) px-4 py-3 ${mono} text-[12px] text-(--paw-paper)`}
        >
          {state.error}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={pending}
        className="paw-btn w-full disabled:opacity-60"
        style={{ padding: "17px", marginTop: "4px" }}
      >
        {pending ? "CREATING…" : "CREATE ACCOUNT"}
      </button>

      <p className={`${mono} text-[11px] leading-relaxed tracking-[0.04em] text-[#5b6068]`}>
        BY CREATING AN ACCOUNT YOU AGREE TO OUR{" "}
        <Link href="/terms" className="text-(--paw-steel-dim) underline">
          TERMS
        </Link>{" "}
        AND{" "}
        <Link href="/privacy" className="text-(--paw-steel-dim) underline">
          PRIVACY POLICY
        </Link>
        .
      </p>
    </form>
  );
}
